import { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";

const emptyForm = {
  nombre: "",
  disciplina: "",
  orden: 1,
  activo: true,
  imagenFile: null,
  imagenURL: "",
};

export default function DeportistasEditor() {
  const [deportistas, setDeportistas] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "deportistas"), orderBy("orden", "asc"));
    return onSnapshot(q, (snap) => {
      setDeportistas(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setForm((f) => ({
      ...f,
      [name]:
        type === "checkbox"
          ? checked
          : type === "file"
          ? files[0]
          : value,
    }));
  };

  const subirImagen = async (file) => {
    const safeName = (file.name || "img").replace(/\s+/g, "_");
    const imgRef = ref(storage, `deportistas/${Date.now()}-${safeName}`);
    await uploadBytes(imgRef, file);
    return await getDownloadURL(imgRef);
  };

  const guardar = async (e) => {
    e.preventDefault();

    let imagenURL = form.imagenURL;

    if (form.imagenFile) {
      imagenURL = await subirImagen(form.imagenFile);
    }

    const data = {
      nombre: form.nombre,
      disciplina: form.disciplina,
      orden: Number(form.orden) || 1,
      activo: form.activo,
      imagenURL,
      updatedAt: serverTimestamp(),
    };

    if (editingId) {
      await updateDoc(doc(db, "deportistas", editingId), data);
    } else {
      await addDoc(collection(db, "deportistas"), {
        ...data,
        createdAt: serverTimestamp(),
      });
    }

    setForm(emptyForm);
    setEditingId(null);
  };

  const editar = (d) => {
    setForm({
      nombre: d.nombre || "",
      disciplina: d.disciplina || "",
      orden: d.orden || 1,
      activo: d.activo ?? true,
      imagenURL: d.imagenURL || "",
      imagenFile: null,
    });
    setEditingId(d.id);
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar deportista?")) return;
    await deleteDoc(doc(db, "deportistas", id));
  };

  if (loading) return <p>Cargando deportistas…</p>;

  return (
    <div className="space-y-6">
      {/* ========================= FORMULARIO ========================= */}
      <form
        onSubmit={guardar}
        className="bg-gray-50 p-4 sm:p-6 rounded-xl space-y-4 border"
      >
        <h3 className="text-lg font-bold text-blue-900">
          {editingId ? "Editar deportista" : "Agregar deportista"}
        </h3>

        {/* NOMBRE */}
        <label className="block">
          <span className="font-semibold text-sm">Nombre</span>
          <input
            name="nombre"
            value={form.nombre}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 mt-1"
            required
          />
        </label>

        {/* DISCIPLINA */}
        <label className="block">
          <span className="font-semibold text-sm">Disciplina</span>
          <input
            name="disciplina"
            value={form.disciplina}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 mt-1"
          />
        </label>

        {/* ORDEN */}
        <label className="block">
          <span className="font-semibold text-sm">
            Orden (posición en la web)
          </span>
          <input
            type="number"
            name="orden"
            value={form.orden}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 mt-1"
          />
        </label>

        {/* IMAGEN ACTUAL */}
        {form.imagenURL && (
          <div>
            <p className="font-semibold text-sm mb-1">Imagen actual</p>
            <img
              src={form.imagenURL}
              alt="Imagen actual"
              className="w-24 h-36 object-cover rounded-lg border shadow"
            />
          </div>
        )}

        {/* SUBIR / CAMBIAR IMAGEN */}
        <label className="block">
          <span className="font-semibold text-sm">
            {form.imagenURL ? "Cambiar imagen" : "Subir imagen"}
          </span>
          <input
            type="file"
            accept="image/*"
            name="imagenFile"
            onChange={handleChange}
            className="mt-1 block w-full text-sm"
          />
        </label>

        {/* ACTIVO */}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            name="activo"
            checked={form.activo}
            onChange={handleChange}
          />
          <span className="text-sm font-medium">Visible en la web</span>
        </label>

        {/* BOTONES */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 w-full sm:w-auto"
          >
            {editingId ? "Guardar cambios" : "Agregar deportista"}
          </button>

          {editingId && (
            <button
              type="button"
              onClick={() => {
                setForm(emptyForm);
                setEditingId(null);
              }}
              className="bg-gray-300 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-400 w-full sm:w-auto"
            >
              Cancelar
            </button>
          )}
        </div>
      </form>

      {/* ========================= LISTA ========================= */}
      <div className="space-y-3">
        {deportistas.map((d) => (
          <div
            key={d.id}
            className="border rounded-xl p-3 sm:p-4 bg-white"
          >
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              {/* BLOQUE IZQUIERDO */}
              <div className="flex items-start gap-3 min-w-0">
                <img
                  src={d.imagenURL}
                  alt={d.nombre}
                  className="w-16 h-24 sm:w-20 sm:h-28 object-cover rounded-lg border shrink-0"
                />

                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-base sm:text-lg break-words">
                    {d.nombre}
                  </p>
                  <p className="text-sm text-gray-500 break-words">
                    {d.disciplina}
                  </p>
                </div>
              </div>

              {/* BLOQUE DERECHO */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:justify-end">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                    d.activo
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {d.activo ? "Activo" : "Inactivo"}
                </span>

                <button
                  onClick={() => editar(d)}
                  className="text-blue-600 hover:underline text-sm whitespace-nowrap"
                >
                  Editar
                </button>

                <button
                  onClick={() => eliminar(d.id)}
                  className="text-red-600 hover:underline text-sm whitespace-nowrap"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        ))}

        {!deportistas.length && (
          <p className="text-sm text-gray-500">
            Aún no hay deportistas creados.
          </p>
        )}
      </div>
    </div>
  );
}